<template>
  <v-container
    style="position: relative"
    v-touch="{
      down: () => swipeRefresh()
    }"
  >
    <FullscreenDialog
      title=""
      v-model="openEventDetails"
    >
      <template slot="content">
        <EventInfo
          :event-item="eventObj"
          :school-images="schoolsLogo"
          :date="selectedDate"
        />
      </template>
      <template slot="action">
        <v-btn
          icon
          color="white"
          @click="openShareSheet(eventObj)"
        >
          <v-icon>mdi-share-variant-outline</v-icon>
        </v-btn>
      </template>
    </FullscreenDialog>
    <v-btn-toggle
      class="calendar-switcher"
      v-model="weekView"
      mandatory
    >
      <v-btn>
        <v-icon>mdi-grid</v-icon>
      </v-btn>
      <v-btn>
        <v-icon>mdi-format-list-bulleted</v-icon>
      </v-btn>
    </v-btn-toggle>
    <template v-if="weekView">
      <WeekPicker @weekChange="modifyWeek" />
      <v-row v-if="selectedWeekEvents.length === 0">
        <v-col class="subtitle-1 mt-1 ml-5 font-weight-medium">
          {{ $t("calendar.noEvents") }}
        </v-col>
      </v-row>
      <v-row v-if="selectedWeekEvents.length > 0">
        <v-col>
          <div
            v-for="(events, index) in groupedWeekEvents"
            :key="index"
          >
            <div
              class="text-body-1 font-weight-medium text-uppercase success--text mb-4"
            >
              {{ index }}
            </div>
            <EventList
              :items="events"
              @openEventDialog="showEventDetails"
              :school-images="schoolsLogo"
            />
          </div>
        </v-col>
      </v-row>
    </template>
    <template v-else>
      <DatePicker
        :picker-with-input="false"
        v-model="picker"
        :items="itemDates"
        @input="showDateEvents"
      />
      <v-row v-if="selectedDateEvents.length === 0">
        <v-col class="subtitle-1 mt-1 ml-5 font-weight-medium">
          {{ $t("calendar.noEvents") }}
        </v-col>
      </v-row>
      <v-row v-if="selectedDateEvents.length > 0">
        <v-col>
          <EventList
            :items="selectedDateEvents"
            @openEventDialog="showEventDetails"
            :school-images="schoolsLogo"
          />
        </v-col>
      </v-row>
    </template>
    <Alert
      :show-alert="error"
      type="error"
      text="Error loading event"
      subtext="Unable to load event, please try again later"
      :timeout="3000"
      position="Bottom"
      @input="onClose"
    />
  </v-container>
</template>
<script>
import EventList from "../components/EventList";
import { Alert, FullscreenDialog } from "@nswdoe/doe-ui-core";
import EventInfo from "../components/EventInfo";
import DatePicker from "../components/DatePicker";
import WeekPicker from "../components/WeekPicker";
import { mapGetters } from "vuex";
import groupBy from "lodash/groupBy";
import {
  FA_SCREEN_VIEW,
  FA_SCREEN_CLASS,
  FA_SCREEN_NAME
} from "@/settings/constants";

const Events = {
  computed: {
    ...mapGetters([
      "events",
      "event",
      "schoolsLogo",
      "appFilters",
      "mySchools"
    ]),
    itemDates: function() {
      let eventDates = [];
      this.events.forEach(event => {
        if (event.multiDay) {
          let dates = this.getIndividualDates(
            new Date(event.startDate),
            new Date(event.endDate)
          );

          dates.forEach(date => {
            // eventDates.indexOf(date) === -1 ? eventDates.push(date) : false
            eventDates.push(date);
          });
        } else {
          // eventDates.indexOf(event.startDate) === -1 ? eventDates.push(event.startDate) : false
          eventDates.push(event.startDate);
        }
      });
      return eventDates;
    },
    selectedDateEvents: function() {
      return this.events
        .filter(event => {
          if (event.multiDay) {
            let dates = this.getIndividualDates(
              new Date(event.startDate),
              new Date(event.endDate)
            );

            return dates.find(date => {
              return date === this.selectedDate;
            });
          } else {
            return event.startDate === this.selectedDate;
          }
        })
        .sort(function(a, b) {
          // move all day events to the top followed by free form
          if (a.allDay) {
            return -1;
          } else if (a.startDateTime && b.startDateTime) {
            return new Date(a.startDateTime) - new Date(b.startDateTime);
          } else if (a.startText != null || b.startText != null) {
            let x = a.startText != null;
            let y = b.startText != null;
            return y - x;
          } else {
            return 0;
          }
        });
    },
    selectedWeekEvents: function() {
      let dates = this.getIndividualDates(
        new Date(this.weekStart),
        new Date(this.weekEnd)
      );
      return this.events
        .filter(event => {
          if (event.multiDay) {
            let eventDates = this.getIndividualDates(
              new Date(event.startDate),
              new Date(event.endDate)
            );
            return eventDates.some(date => {
              return dates.includes(date);
            });
          } else {
            return dates.includes(event.startDate);
          }
        })
        .sort()
        .reverse();
    },
    groupedWeekEvents: function() {
      return groupBy(this.selectedWeekEvents, event => {
        return new Date(event.startDate).toLocaleString(this.getLocale(), {
          weekday: "long",
          month: "long",
          day: "numeric"
        });
      });
    },
    appBuild: function() {
      return this.getCookie("appbuild");
    }
  },
  components: {
    EventList,
    FullscreenDialog,
    DatePicker,
    WeekPicker,
    EventInfo,
    Alert
  },
  name: "Events",
  title: "Events",
  created: function() {
    //eslint-disable-next-line
    NativeJS.debugLog("test debug on events page load");
    if (this.$route.params.id) {
      // opens an event from a route, this only happens when user clicks on a reminder notification
      this.$store
        .dispatch("getIndividualEvent", this.$route.params.id)
        .then(() => {
          const schoolName = this.schoolName(this.event.domain);
          const schoolCode = this.schoolCode(this.event.domain);
          this.analytics("calendar_reminder_push_notification", {
            item_category: "calendar",
            label: this.event.title,
            school_name: schoolName,
            school_code: schoolCode
          });
          this.$router.push("/events");
          this.showEventDetails(this.event);
        })
        .catch(() => {
          this.error = true;
        });
    } else {
      this.analytics(FA_SCREEN_VIEW, {
        [FA_SCREEN_CLASS]: "calendar",
        [FA_SCREEN_NAME]: "calendar_view"
      });
      this.$store.dispatch("getEvents");
    }
  },
  data() {
    return {
      selectedDate: null,
      openEventDetails: false,
      picker: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substr(0, 10),
      eventObj: {},
      weekView: true,
      weekStart: "",
      weekEnd: "",
      error: false
    };
  },
  methods: {
    showEventDetails(eventItem) {
      this.$store.dispatch("setEventRead", eventItem.id);
      this.eventObj = eventItem;
      this.openEventDetails = true;
      this.analytics(FA_SCREEN_VIEW, {
        [FA_SCREEN_CLASS]: "calendar",
        [FA_SCREEN_NAME]: eventItem.title.replaceAll(" ", "_")
      });
    },
    showDateEvents(date) {
      this.selectedDate = date;
    },
    applyFilters() {
      this.page = 1;
      this.$store.dispatch("getEvents");
    },
    getIndividualDates(startDate, endDate) {
      const dates = [];
      let currentDate = startDate;

      const addDays = function(days) {
        const date = new Date(this.valueOf());
        date.setDate(date.getDate() + days);
        return date;
      };

      while (currentDate <= endDate) {
        dates.push(this.formatDate(currentDate));
        currentDate = addDays.call(currentDate, 1);
      }
      return dates;
    },
    swipeRefresh() {
      if (window.pageYOffset === 0) {
        this.$store.dispatch("getEvents");
      }
    },
    openShareSheet(eventObj) {
      //eslint-disable-next-line
      NativeJS.debugLog(`sharing ${eventObj.title} at url ${eventObj.html}`);
      //eslint-disable-next-line
      NativeJS.openShareSheet(eventObj.html, eventObj.title);
    },
    modifyWeek(dates) {
      this.weekStart = dates.start;
      this.weekEnd = dates.end;
    },
    onClose: function() {
      this.error = false;
    },
    schoolName(id) {
      if (id != "global") {
        return this.mySchools.find(school => school.schoolId == id)?.details
          .name;
      }

      return "NSW Department of Education";
    },
    schoolCode(id) {
      if (id != "global") {
        return this.mySchools.find(school => school.schoolId == id)?.schoolCode;
      }

      return "NSW Department of Education";
    }
  },
  watch: {
    appFilters: {
      deep: true,
      handler() {
        this.applyFilters();
      }
    },
    openEventDetails: {
      handler() {
        if (!this.openEventDetails) {
          this.analytics("notifications_inbox", {
            event_action: "dismiss_calendar_notification",
            school_code: this.schoolCode(this.eventObj.domain),
            school_name: this.schoolName(this.eventObj.domain),
            content_title: this.eventObj.title,
            content_id: this.eventObj.id
          });
        }
      }
    },
    $route: {
      handler(to) {
        if (to.params.id) {
          this.$store
            .dispatch("getIndividualEvent", this.$route.params.id)
            .then(() => {
              this.$router.push("/events");
              this.showEventDetails(this.event);
            })
            .catch(() => {
              this.error = true;
            });
        }
      }
    }
  }
};
export default Events;
</script>
<style lang="scss" scoped>
div::v-deep {
  .v-picker__title {
    padding-left: 0;
    padding-right: 0;
  }
  .v-date-picker-header {
    padding-left: 0;
    padding-right: 0;
  }
  .v-date-picker-table {
    padding-left: 0;
    padding-right: 0;
  }
}
.calendar-switcher {
  position: absolute;
  right: 0;
  top: 37px;
  z-index: 1;
  .v-btn {
    height: 40px !important;
    min-width: 40px !important;
    padding: 0 7px !important;
  }
}
</style>
